'use client';

import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import api from "@/app/lib/axios";
import { Product as ApiProduct, DisplayProduct } from "@/types/product";
import ProductCard from "@/app/store/components/ProductCard";
import MatrixGrid from '@/app/components/MatrixGrid';
import Pagination from "@/app/store/components/Pagination";
import PurchaseModal from "@/app/store/components/PurchaseModal";
import FAQSection from "@/app/components/FaqSection";
import TaC from "@/app/components/TermsAndConditions";
import { useAuth } from "@/app/hooks/context/AuthContext";
import { ChevronDown } from 'lucide-react';

type SortOrder = 'price-asc' | 'price-desc';

const formatPrice = (priceInCents: number) => priceInCents.toLocaleString();

const mapApiProductsToDisplayProducts = (apiProducts: ApiProduct[]): DisplayProduct[] => {
    return apiProducts.map(p => ({
        ...p,
        gradient: 'from-blue-800 to-sky-700',
        valueDisplay: formatPrice(p.final_price ?? p.price),
        originalValueDisplay: p.discount_percent > 0 ? formatPrice(p.price) : undefined,
        badge: p.virtual ? 'DIGITAL' : 'PHYSICAL',
    }));
};

// Memoized Sort Button Component
const SortButton = memo(({
    option,
    isActive,
    onClick
}: {
    option: { value: string; label: string };
    isActive: boolean;
    onClick: () => void;
}) => (
    <button
        onClick={onClick}
        className={`w-full px-6 py-3 text-left font-mono text-sm transition-all duration-200 ${isActive
            ? 'bg-emerald-700/50 text-gray-200 font-bold'
            : 'text-gray-200 hover:bg-emerald-700/50'
            }`}
    >
        {option.label}
    </button>
));
SortButton.displayName = 'SortButton';

// Memoized Info Button Component
const InfoButton = memo(({ onClick, label }: { onClick: () => void; label: string }) => (
    <button
        onClick={onClick}
        className="text-gray-200 text-center text-sm group flex items-center gap-3 px-6 py-3 lg:px-10 lg:py-4 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-300"
        aria-label={label}
    >
        <span className='font-mono font-semibold'>{label}</span>
    </button>
));
InfoButton.displayName = 'InfoButton';

// Memoized Terms Modal Component
const TermsModal = memo(({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-gray-900 shadow-sm rounded-2xl shadow-2xl w-full max-w-2xl my-4 sm:my-8 overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6 shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <span className="text-2xl font-bold">×</span>
                    </button>
                    <h2 className="text-2xl font-bold text-white">Terms & Conditions</h2>
                    <p className="text-green-100 text-sm mt-1">
                        Please read these terms carefully before using our services.
                    </p>
                </div>
                <div className="overflow-y-auto">
                    <TaC />
                </div>
            </div>
        </div>
    );
});
TermsModal.displayName = 'TermsModal';

export default function StorePage() {
    const { user, refreshUser } = useAuth();
    const [products, setProducts] = useState<DisplayProduct[]>([]);
    const [sortOrder, setSortOrder] = useState<SortOrder>('price-desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<DisplayProduct | null>(null);
    const [showTerms, setShowTerms] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Prevent body scroll when terms modal is open
    useEffect(() => {
        if (showTerms) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showTerms]);

    // Memoized fetch function with AbortController
    const fetchProducts = useCallback(async (page: number, sort: SortOrder, signal?: AbortSignal) => {
        setIsLoading(true);
        try {
            const sortParam = sort === 'price-desc' ? 'price:desc' : 'price:asc';
            const response = await api.get('products', {
                params: { page, sort: sortParam },
                signal
            });
            const apiData = response.data;
            const activeProducts = apiData.data.filter((p: ApiProduct) => p.status);
            setProducts(mapApiProductsToDisplayProducts(activeProducts));
            setCurrentPage(apiData.current_page);
            setLastPage(apiData.last_page);
        } catch (error) {
            // Don't set empty products if request was aborted
            if (error instanceof Error && error.name !== 'CanceledError') {
                setProducts([]);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch products with cleanup
    useEffect(() => {
        const controller = new AbortController();
        fetchProducts(currentPage, sortOrder, controller.signal);

        return () => {
            controller.abort();
        };
    }, [currentPage, sortOrder, fetchProducts]);

    // Memoized handlers
    const handleSortChange = useCallback((order: SortOrder) => {
        if (sortOrder !== order) {
            setSortOrder(order);
            setCurrentPage(1);
            setDropdownOpen(false);
        }
    }, [sortOrder]);

    const handlePageChange = useCallback((page: number) => {
        if (page > 0 && page <= lastPage && page !== currentPage) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [lastPage, currentPage]);

    const handleBuyNow = useCallback((product: DisplayProduct) => {
        setSelectedProduct(product);
        setModalOpen(true);
    }, []);

    const handlePurchaseSuccess = useCallback(() => {
        fetchProducts(currentPage, sortOrder);
        refreshUser();
    }, [fetchProducts, currentPage, sortOrder, refreshUser]);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleToggleDropdown = useCallback(() => {
        setDropdownOpen(prev => !prev);
    }, []);

    const handleCloseDropdown = useCallback(() => {
        setDropdownOpen(false);
    }, []);

    const handleShowTerms = useCallback(() => {
        setShowTerms(true);
    }, []);

    const handleCloseTerms = useCallback(() => {
        setShowTerms(false);
    }, []);

    const handleScrollToFAQ = useCallback(() => {
        const faqSection = document.getElementById("faq");
        if (faqSection) {
            faqSection.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    // Memoized sort options
    const sortOptions = useMemo(() => [
        { value: 'price-desc', label: 'Kick Points: High to Low' },
        { value: 'price-asc', label: 'Kick Points: Low to High' }
    ], []);

    const currentSortLabel = useMemo(() =>
        sortOptions.find(opt => opt.value === sortOrder)?.label || 'Sort by Kick Points',
        [sortOptions, sortOrder]
    );

    // Memoized pagination controls
    const paginationControls = useMemo(() => (
        !isLoading && lastPage > 1 && (
            <Pagination
                currentPage={currentPage}
                lastPage={lastPage}
                onPageChange={handlePageChange}
            />
        )
    ), [isLoading, lastPage, currentPage, handlePageChange]);

    return (
        <div className="mt-15 text-gray-200 min-h-screen overflow-hidden relative">
            <MatrixGrid />
            <div className="relative z-10 max-w-7xl mx-auto px-4 py-16">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-6xl font-mono font-bold tracking-widest mb-4">
                        <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                            STORE
                        </span>
                    </h1>
                    <p className="text-green-300 font-mono text-sm md:text-base">
                        <span className="inline-block animate-pulse">{'['}</span> EARN KICK POINTS BY WATCHING STREAMS <span className="inline-block animate-pulse">{']'}</span>
                    </p>

                    {/* Info Buttons */}
                    <div className="text-center mt-6 flex justify-center gap-4">
                        <InfoButton onClick={handleShowTerms} label="Read Terms" />
                        <InfoButton onClick={handleScrollToFAQ} label="Read FAQ" />
                    </div>
                </div>

                {/* Sorting Dropdown */}
                <div className="mb-12 flex items-center justify-center backdrop-blur rounded-xl p-4 relative z-30">
                    <div className="relative">
                        <button
                            onClick={handleToggleDropdown}
                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 min-w-[220px] justify-between"
                        >
                            <span className="text-gray-200 font-mono font-semibold text-sm">
                                {currentSortLabel}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-200 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {dropdownOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={handleCloseDropdown}
                                />

                                <div className="absolute top-full mt-2 w-full bg-gradient-to-br from-emerald-900 to-gray-900 rounded-lg shadow-xl overflow-hidden z-50">
                                    {sortOptions.map((option) => (
                                        <SortButton
                                            key={option.value}
                                            option={option}
                                            isActive={sortOrder === option.value}
                                            onClick={() => handleSortChange(option.value as SortOrder)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* TOP PAGINATION */}
                <div className="mb-8">{paginationControls}</div>

                {/* Products Grid */}
                {isLoading ? (
                    <div className="text-center text-xl text-gray-200 font-mono py-20">Loading Products... ⏳</div>
                ) : products.length === 0 ? (
                    <div className="text-center text-xl text-gray-400 font-mono py-20">No products found. 😔</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onBuyNow={handleBuyNow}
                                user={user}
                            />
                        ))}
                    </div>
                )}

                {/* BOTTOM PAGINATION */}
                <div className="mt-12">{paginationControls}</div>

                <FAQSection />
            </div>

            {/* Terms and Conditions Popup */}
            <TermsModal isOpen={showTerms} onClose={handleCloseTerms} />

            {/* Buy Now Modal */}
            {selectedProduct && (
                <PurchaseModal
                    product={selectedProduct}
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSuccess={handlePurchaseSuccess}
                />
            )}
        </div>
    );
}